<template>
  <div class="glass-card p-4">
    <div class="flex justify-between items-center mb-4">
      <h3 class="font-bold flex items-center gap-2">
        <div class="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
          </svg>
        </div>
        Task Manager
      </h3>
      <button @click="showCreateTask = true" class="accent-btn text-sm">
        + New Task
      </button>
    </div>

    <!-- Task Stats -->
    <div class="grid grid-cols-4 gap-2 mb-4">
      <div class="text-center p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
        <p class="text-lg font-bold text-blue-600">{{ taskStats.pending }}</p>
        <p class="text-xs text-blue-600">Pending</p>
      </div>
      <div class="text-center p-2 rounded-lg bg-green-50 dark:bg-green-900/20">
        <p class="text-lg font-bold text-green-600">{{ taskStats.active }}</p>
        <p class="text-xs text-green-600">Active</p>
      </div>
      <div class="text-center p-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
        <p class="text-lg font-bold text-yellow-600">{{ taskStats.completed }}</p>
        <p class="text-xs text-yellow-600">Completed</p>
      </div>
      <div class="text-center p-2 rounded-lg bg-red-50 dark:bg-red-900/20">
        <p class="text-lg font-bold text-red-600">{{ taskStats.failed }}</p>
        <p class="text-xs text-red-600">Failed</p>
      </div>
    </div>

    <!-- Task List -->
    <div class="space-y-2 max-h-64 overflow-y-auto">
      <div v-for="task in tasks" :key="task.id" 
           class="p-3 rounded-lg border transition-all hover:shadow-md cursor-pointer"
           :class="getTaskBorderClass(task.status)"
           @click="selectTask(task)">
        <div class="flex justify-between items-start mb-2">
          <div class="flex-1">
            <h4 class="font-medium text-sm">{{ task.title }}</h4>
            <p class="text-xs themed-text-secondary mt-1">{{ task.description }}</p>
          </div>
          <span :class="getStatusBadgeClass(task.status)" class="text-xs px-2 py-1 rounded-full">
            {{ task.status }}
          </span>
        </div>
        
        <div class="flex justify-between items-center text-xs">
          <div class="flex items-center gap-2">
            <span class="opacity-70">Field:</span>
            <span>{{ task.field }}</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="opacity-70">Drone:</span>
            <span>{{ task.drone || 'Auto' }}</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="opacity-70">Priority:</span>
            <span :class="getPriorityClass(task.priority)">{{ task.priority }}</span>
          </div>
        </div>

        <!-- Progress Bar -->
        <div v-if="task.status === 'active'" class="mt-2">
          <div class="flex justify-between text-xs mb-1">
            <span>Progress</span>
            <span>{{ task.progress }}%</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-1">
            <div class="bg-green-500 h-1 rounded-full transition-all" :style="`width: ${task.progress}%`"></div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div v-if="task.status === 'pending'" class="mt-2 flex gap-2">
          <button @click.stop="startTask(task)" class="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600">
            Start
          </button>
          <button @click.stop="editTask(task)" class="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600">
            Edit
          </button>
          <button @click.stop="deleteTask(task)" class="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600">
            Delete
          </button>
        </div>

        <div v-if="task.status === 'active'" class="mt-2 flex gap-2">
          <button @click.stop="pauseTask(task)" class="text-xs bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600">
            Pause
          </button>
          <button @click.stop="completeTask(task)" class="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600">
            Complete
          </button>
        </div>
      </div>
    </div>

    <!-- Create Task Modal -->
    <div v-if="showCreateTask" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="glass-card p-6 max-w-md w-full">
        <h3 class="text-lg font-bold mb-4">Create New Task</h3>
        
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium mb-1">Task Title</label>
            <input v-model="newTask.title" type="text" class="input" placeholder="Enter task title">
          </div>
          
          <div>
            <label class="block text-sm font-medium mb-1">Description</label>
            <textarea v-model="newTask.description" class="input" rows="3" placeholder="Enter task description"></textarea>
          </div>
          
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium mb-1">Field</label>
              <select v-model="newTask.field" class="input">
                <option value="">Select Field</option>
                <option value="North Field Alpha">North Field Alpha</option>
                <option value="East Field Beta">East Field Beta</option>
                <option value="South Field Gamma">South Field Gamma</option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium mb-1">Drone</label>
              <select v-model="newTask.drone" class="input">
                <option value="Auto">Auto Assign</option>
                <option value="D1">Alpha One</option>
                <option value="D2">Beta Two</option>
                <option value="D3">Gamma Three</option>
              </select>
            </div>
          </div>
          
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium mb-1">Priority</label>
              <select v-model="newTask.priority" class="input">
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium mb-1">Task Type</label>
              <select v-model="newTask.type" class="input">
                <option value="spraying">Spraying</option>
                <option value="monitoring">Monitoring</option>
                <option value="mapping">Mapping</option>
                <option value="survey">Survey</option>
              </select>
            </div>
          </div>
        </div>
        
        <div class="flex justify-end gap-2 mt-6">
          <button @click="showCreateTask = false" class="px-4 py-2 text-gray-600 hover:text-gray-800">
            Cancel
          </button>
          <button @click="createTask" class="accent-btn">
            Create Task
          </button>
        </div>
      </div>
    </div>

    <!-- Task Details Modal -->
    <div v-if="selectedTask" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="glass-card p-6 max-w-md w-full">
        <h3 class="text-lg font-bold mb-4">Task Details</h3>
        
        <div class="space-y-3">
          <div>
            <span class="text-sm opacity-70">Title:</span>
            <p class="font-medium">{{ selectedTask.title }}</p>
          </div>
          
          <div>
            <span class="text-sm opacity-70">Description:</span>
            <p class="text-sm">{{ selectedTask.description }}</p>
          </div>
          
          <div class="grid grid-cols-2 gap-4">
            <div>
              <span class="text-sm opacity-70">Status:</span>
              <p :class="getStatusBadgeClass(selectedTask.status)" class="text-xs px-2 py-1 rounded-full inline-block">
                {{ selectedTask.status }}
              </p>
            </div>
            
            <div>
              <span class="text-sm opacity-70">Priority:</span>
              <p :class="getPriorityClass(selectedTask.priority)" class="text-xs font-medium">
                {{ selectedTask.priority }}
              </p>
            </div>
          </div>
          
          <div class="grid grid-cols-2 gap-4">
            <div>
              <span class="text-sm opacity-70">Field:</span>
              <p class="text-sm">{{ selectedTask.field }}</p>
            </div>
            
            <div>
              <span class="text-sm opacity-70">Drone:</span>
              <p class="text-sm">{{ selectedTask.drone || 'Auto' }}</p>
            </div>
          </div>
          
          <div v-if="selectedTask.createdAt">
            <span class="text-sm opacity-70">Created:</span>
            <p class="text-sm">{{ formatDate(selectedTask.createdAt) }}</p>
          </div>
          
          <div v-if="selectedTask.status === 'active' && selectedTask.progress !== undefined">
            <div>
              <span class="text-sm opacity-70">Progress:</span>
              <div class="mt-1">
                <div class="flex justify-between text-xs mb-1">
                  <span>{{ selectedTask.progress }}%</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div class="bg-green-500 h-2 rounded-full transition-all" :style="`width: ${selectedTask.progress}%`"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="flex justify-end mt-6">
          <button @click="selectedTask = null" class="accent-btn">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const showCreateTask = ref(false)
const selectedTask = ref(null)

const tasks = ref([
  {
    id: 1,
    title: 'Spray North Field Alpha',
    description: 'Apply pesticide for pest control',
    status: 'pending',
    priority: 'High',
    field: 'North Field Alpha',
    drone: 'D1',
    type: 'spraying',
    progress: 0,
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000)
  },
  {
    id: 2,
    title: 'Monitor East Field Beta',
    description: 'Check crop health and growth patterns',
    status: 'active',
    priority: 'Medium',
    field: 'East Field Beta',
    drone: 'D2',
    type: 'monitoring',
    progress: 65,
    createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000)
  },
  {
    id: 3,
    title: 'Map South Field Gamma',
    description: 'Create detailed field map for planning',
    status: 'completed',
    priority: 'Low',
    field: 'South Field Gamma',
    drone: 'D3',
    type: 'mapping',
    progress: 100,
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000)
  }
])

const newTask = ref({
  title: '',
  description: '',
  field: '',
  drone: 'Auto',
  priority: 'Medium',
  type: 'spraying'
})

const taskStats = computed(() => ({
  pending: tasks.value.filter(t => t.status === 'pending').length,
  active: tasks.value.filter(t => t.status === 'active').length,
  completed: tasks.value.filter(t => t.status === 'completed').length,
  failed: tasks.value.filter(t => t.status === 'failed').length
}))

const getTaskBorderClass = (status) => {
  const classes = {
    pending: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20',
    active: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20',
    completed: 'border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/20',
    failed: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
  }
  return classes[status] || classes.pending
}

const getStatusBadgeClass = (status) => {
  const classes = {
    pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    completed: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
  }
  return classes[status] || classes.pending
}

const getPriorityClass = (priority) => {
  const classes = {
    Low: 'text-blue-600',
    Medium: 'text-yellow-600',
    High: 'text-orange-600',
    Critical: 'text-red-600'
  }
  return classes[priority] || classes.Medium
}

const selectTask = (task) => {
  selectedTask.value = task
}

const createTask = () => {
  const task = {
    id: Date.now(),
    ...newTask.value,
    status: 'pending',
    progress: 0,
    createdAt: new Date()
  }
  
  tasks.value.unshift(task)
  showCreateTask.value = false
  
  // Reset form
  newTask.value = {
    title: '',
    description: '',
    field: '',
    drone: 'Auto',
    priority: 'Medium',
    type: 'spraying'
  }
}

const startTask = (task) => {
  task.status = 'active'
  task.progress = 0
  simulateProgress(task)
}

const pauseTask = (task) => {
  task.status = 'pending'
}

const completeTask = (task) => {
  task.status = 'completed'
  task.progress = 100
}

const editTask = (task) => {
  selectedTask.value = task
}

const deleteTask = (task) => {
  const index = tasks.value.findIndex(t => t.id === task.id)
  if (index > -1) {
    tasks.value.splice(index, 1)
  }
}

const simulateProgress = (task) => {
  const interval = setInterval(() => {
    if (task.status === 'active' && task.progress < 100) {
      task.progress = Math.min(100, task.progress + Math.random() * 10)
    } else {
      clearInterval(interval)
    }
  }, 2000)
}

const formatDate = (date) => {
  return new Date(date).toLocaleString('th-TH', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  })
}

onMounted(() => {
  // Simulate real-time updates
  const interval = setInterval(() => {
    tasks.value.forEach(task => {
      if (task.status === 'active' && Math.random() > 0.8) {
        task.progress = Math.min(100, task.progress + Math.random() * 5)
        if (task.progress >= 100) {
          task.status = 'completed'
        }
      }
    })
  }, 5000)
  
  onUnmounted(() => {
    clearInterval(interval)
  })
})
</script>
